import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
//
import { FaCopy } from 'react-icons/fa'; //

import { Input } from '../../../components/ui';
//
import { copySO, getOrderList } from '../../../api/services/orderService';
import { setShowLoading } from '../../../redux/systemInfo';
import { getCartList } from '../../../api/services/cartService';
import { setUserCartList } from '../../../redux/userCart';

const Title = styled.h1`
  width: 100%;
  font-size: 24px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputLabel = styled.label`
  font-size: 14px;
  color: #444;
`;

const InputContainer = styled.div`
  width: 100%;
  border: 1px solid #ccc;
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  margin-bottom: 2rem;
  transition: background-color 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const OrderListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid #ddd;
  font-size: 14px;
  margin-top: 1rem;
  gap: 1rem;
`;

const OrderListItem = styled.div`
  display: flex;
  padding: 16px 16px;
  width: 100%;
  justify-content: space-between;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.colors.primaryText};
`;

const CustPONumber = styled.div`
  font-weight: bold;
  color: ${(props) => props.theme.colors.primaryBlue};
  margin-bottom: 4px;
`;

const OrderDate = styled.div`
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.secondaryText};
`;

// 가운데 영역
const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OrderNumber = styled.div`
  font-weight: bold;
  color: ${(props) => props.theme.colors.primaryBlue};
  margin-bottom: 4px;
`;

const StatusText = styled.div`
  font-style: italic;
  color: ${(props) => props.theme.colors.menuText};
`;

// 오른쪽 영역
const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const TotalAmount = styled.div`
  font-weight: bold;
  color: ${(props) => props.theme.colors.primaryBlue};
  margin-bottom: 4px;
`;

const ViewDetailLink = styled(Link)`
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.primaryBlue};
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const NoOrdersMessage = styled.div`
  text-align: center;
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 16px;
  padding: 2rem;
  border: 1px solid #eee;
  border-radius: 4px;
`;

const CopyButton = styled.button`
  display: flex;
  background-color: ${(props) => props.theme.colors.primaryBlue};
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px;
  border: none;
  border-radius: 4px;
  gap: 10px;
  cursor: pointer;

  &:hover {
    background-color: #2b2370;
  }
`;

const OrderView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo);

  const [orderList, setOrderList] = useState([]);
  const [filter, setFilter] = useState({
    from: '',
    to: '',
    orderNo: '',
    sku: '',
    address: '',
  });

  const getDbOrderList = async () => {
    const result = await getOrderList(userInfo.idCustomer, filter);
    setOrderList(result.orderList);
  };

  const getDbCartList = async () => {
    dispatch(setShowLoading({ showLoading: true }));
    if (userInfo.zipcode) {
      const result = await getCartList(
        userInfo.idCustomer,
        userInfo.zipcode,
        userInfo.idStore,
      );
      dispatch(setUserCartList({ cart_list: result.cartList }));
    }
    dispatch(setShowLoading({ showLoading: false }));
  };

  const handleCopyOrder = async (idSO) => {
    const result = await copySO(userInfo.idCustomer, idSO);
    if (result.is_success) {
      await getDbCartList();
    }
    navigate(`/checkout?from=${idSO}`);
  };

  return (
    <>
      <Title>Search Web Orders</Title>

      <InputGrid>
        <InputGroup>
          <InputLabel>From Date</InputLabel>
          <InputContainer>
            <Input
              type='date'
              placeholder='month/day/year'
              value={filter.from}
              onChange={(value) => {
                setFilter({
                  ...filter,
                  from: value,
                });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>To Date</InputLabel>
          <InputContainer>
            <Input
              type='date'
              placeholder='month/day/year'
              value={filter.to}
              onChange={(value) => {
                setFilter({
                  ...filter,
                  to: value,
                });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>Purchase Order Number</InputLabel>
          <InputContainer>
            <Input
              placeholder='Enter Purchase Order Number'
              value={filter.orderNo}
              onChange={(value) => {
                setFilter({
                  ...filter,
                  orderNo: value,
                });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>SKU</InputLabel>
          <InputContainer>
            <Input
              placeholder='Enter Product SKU'
              value={filter.sku}
              onChange={(value) => {
                setFilter({
                  ...filter,
                  sku: value,
                });
              }}
            />
          </InputContainer>
        </InputGroup>
      </InputGrid>

      <InputGroup style={{ width: '100%', marginBottom: '2rem' }}>
        <InputLabel>Shipping Address1</InputLabel>
        <InputContainer style={{ width: '100%' }}>
          <Input
            placeholder='Enter Shipping Address1'
            value={filter.address}
            onChange={(value) => {
              setFilter({
                ...filter,
                address: value,
              });
            }}
          />
        </InputContainer>
      </InputGroup>

      <SearchButton onClick={getDbOrderList}>SEARCH</SearchButton>

      {orderList.length > 0 ? (
        <OrderListContainer>
          {orderList.map((order) => {
            return (
              <OrderListItem key={order.idSO}>
                <LeftSection>
                  <CustPONumber>Cust PO # {order.custPO}</CustPONumber>
                  <OrderDate>{order.orderDT}</OrderDate>
                </LeftSection>

                <OrderNumber>Order # {order.idSO}</OrderNumber>
                <StatusText>{order.idStyle}</StatusText>
                <CenterSection>
                  <StatusText>
                    Status: {order.status}
                    <br />
                    Payment:{' '}
                    {order.paidStatus === '0'
                      ? 'UNPAID'
                      : order.paidStatus === '1'
                        ? 'PARTIALLY PAID'
                        : order.paidStatus === '2'
                          ? 'FULLY PAID'
                          : ''}
                  </StatusText>
                </CenterSection>
                <RightSection>
                  <TotalAmount>
                    Total: ${parseFloat(order.grandAmount).toFixed(2)}
                  </TotalAmount>
                  <ViewDetailLink to={`/order/${order.idSO}`}>
                    View Detail
                  </ViewDetailLink>
                </RightSection>
                <CopyButton
                  onClick={() => {
                    handleCopyOrder(order.idSO);
                  }}
                >
                  <FaCopy />
                  <div>Copy Order</div>
                </CopyButton>
              </OrderListItem>
            );
          })}
        </OrderListContainer>
      ) : (
        <NoOrdersMessage>No orders</NoOrdersMessage>
      )}
    </>
  );
};

export default OrderView;
